import React, { useState, useMemo } from "react";
import "./App.css";
import quoteItemsData from "./quoteItems.json";

function App() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [tagFilter, setTagFilter] = useState("all");

  // Get all unique tags from the data
  const allTags = useMemo(() => {
    const tags = new Set();
    quoteItemsData.forEach((item) => {
      if (item.tags) {
        item.tags.forEach((tag) => tags.add(tag));
      }
    });
    return Array.from(tags).sort();
  }, []);

  // Filter items based on search term and tag filter
  const filteredItems = useMemo(() => {
    let filtered = quoteItemsData.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (tagFilter !== "all") {
      filtered = filtered.filter((item) => {
        return item.tags && item.tags.includes(tagFilter);
      });
    }

    return filtered;
  }, [searchTerm, tagFilter]);

  // Calculate total cost
  const totalCost = useMemo(() => {
    return selectedItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
  }, [selectedItems]);

  // Add item to quote
  const addToQuote = (item) => {
    if (!selectedItems.find((selected) => selected.id === item.id)) {
      setSelectedItems([...selectedItems, { ...item, quantity: 1 }]);
    }
  };

  // Remove item from quote
  const removeFromQuote = (itemId) => {
    setSelectedItems(selectedItems.filter((item) => item.id !== itemId));
  };

  // Clear all items from quote
  const clearQuote = () => {
    setSelectedItems([]);
  };

  // Update quantity for an item
  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setSelectedItems(
      selectedItems.map((item) =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f9fafb",
        padding: "20px",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <header style={{ textAlign: "center", marginBottom: "40px" }}>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              color: "#1f2937",
              marginBottom: "10px",
            }}
          >
            Website Development Quote Calculator
          </h1>
          <p style={{ color: "#6b7280" }}>
            Select services to build your custom website development quote
          </p>
        </header>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: "40px",
          }}
        >
          {/* Available Items */}
          <div>
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                padding: "24px",
                marginBottom: "24px",
              }}
            >
              <h2
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "600",
                  color: "#1f2937",
                  marginBottom: "20px",
                }}
              >
                Available Services
              </h2>

              {/* Search and Filter Controls */}
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  marginBottom: "24px",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: "1", minWidth: "200px" }}>
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                <div>
                  <select
                    value={tagFilter}
                    onChange={(e) => setTagFilter(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="all">All Tags</option>
                    {allTags.map((tag) => (
                      <option key={tag} value={tag}>
                        {tag.charAt(0).toUpperCase() + tag.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Available Items Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "20px",
                }}
              >
                {filteredItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      padding: "20px",
                      backgroundColor: "white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      transition: "all 0.2s",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                      e.target.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                      e.target.style.transform = "translateY(0)";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <h3
                        style={{
                          fontWeight: "bold",
                          color: "#1f2937",
                          fontSize: "1.1rem",
                          margin: "0",
                          lineHeight: "1.3",
                        }}
                      >
                        {item.name}
                      </h3>
                      <div
                        style={{
                          backgroundColor: "#dcfce7",
                          color: "#166534",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "0.9rem",
                          fontWeight: "600",
                          marginLeft: "8px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ${item.price.toLocaleString()}
                      </div>
                    </div>

                    <p
                      style={{
                        color: "#6b7280",
                        fontSize: "0.9rem",
                        marginBottom: "16px",
                        lineHeight: "1.5",
                      }}
                    >
                      {item.description}
                    </p>

                    {item.pages && item.pages.trim() && (
                      <div
                        style={{
                          marginBottom: "12px",
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          backgroundColor: "#f0f9ff",
                          padding: "8px 12px",
                          borderRadius: "6px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            marginBottom: "4px",
                            color: "#1e40af",
                          }}
                        >
                          📄 Pages:
                        </div>
                        <div>{item.pages}</div>
                      </div>
                    )}

                    {item.blocks && item.blocks.trim() && (
                      <div
                        style={{
                          marginBottom: "12px",
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          backgroundColor: "#f0fdf4",
                          padding: "8px 12px",
                          borderRadius: "6px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            marginBottom: "4px",
                            color: "#166534",
                          }}
                        >
                          🧱 Blocks:
                        </div>
                        <div>{item.blocks}</div>
                      </div>
                    )}

                    {item.tags && item.tags.length > 0 && (
                      <div
                        style={{
                          marginBottom: "12px",
                          fontSize: "0.8rem",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            marginBottom: "4px",
                            color: "#374151",
                          }}
                        >
                          🏷️ Tags:
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          {item.tags.map((tag) => (
                            <span
                              key={tag}
                              style={{
                                backgroundColor: "#e0e7ff",
                                color: "#3730a3",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontSize: "0.7rem",
                                fontWeight: "500",
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "16px",
                        fontSize: "0.8rem",
                        color: "#6b7280",
                        backgroundColor: "#f9fafb",
                        padding: "8px 12px",
                        borderRadius: "6px",
                      }}
                    >
                      <span style={{ marginRight: "8px" }}>⏱️</span>
                      {item.timeToImplement}
                    </div>

                    <button
                      onClick={() => addToQuote(item)}
                      disabled={selectedItems.find(
                        (selected) => selected.id === item.id
                      )}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        fontWeight: "600",
                        fontSize: "0.9rem",
                        border: "2px solid",
                        cursor: selectedItems.find(
                          (selected) => selected.id === item.id
                        )
                          ? "not-allowed"
                          : "pointer",
                        backgroundColor: selectedItems.find(
                          (selected) => selected.id === item.id
                        )
                          ? "#f3f4f6"
                          : "#2563eb",
                        color: selectedItems.find(
                          (selected) => selected.id === item.id
                        )
                          ? "#6b7280"
                          : "white",
                        borderColor: selectedItems.find(
                          (selected) => selected.id === item.id
                        )
                          ? "#d1d5db"
                          : "#2563eb",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        if (
                          !selectedItems.find(
                            (selected) => selected.id === item.id
                          )
                        ) {
                          e.target.style.backgroundColor = "#1d4ed8";
                          e.target.style.borderColor = "#1d4ed8";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (
                          !selectedItems.find(
                            (selected) => selected.id === item.id
                          )
                        ) {
                          e.target.style.backgroundColor = "#2563eb";
                          e.target.style.borderColor = "#2563eb";
                        }
                      }}
                    >
                      {selectedItems.find((selected) => selected.id === item.id)
                        ? "✓ Added to Quote"
                        : "+ Add to Quote"}
                    </button>
                  </div>
                ))}
              </div>

              {filteredItems.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#6b7280",
                  }}
                >
                  No services found matching your criteria.
                </div>
              )}
            </div>
          </div>

          {/* Quote Summary */}
          <div>
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                padding: "24px",
                position: "sticky",
                top: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h2
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "600",
                    color: "#1f2937",
                    margin: "0",
                  }}
                >
                  Your Quote
                </h2>
                {selectedItems.length > 0 && (
                  <button
                    onClick={clearQuote}
                    style={{
                      color: "#dc2626",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Clear All
                  </button>
                )}
              </div>

              {selectedItems.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#6b7280",
                  }}
                >
                  <p style={{ margin: "0 0 8px 0" }}>No items selected yet.</p>
                  <p style={{ fontSize: "0.9rem", margin: "0" }}>
                    Add services to see your quote.
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: "24px" }}>
                    {selectedItems.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          padding: "12px",
                          backgroundColor: "#f9fafb",
                          borderRadius: "8px",
                          marginBottom: "12px",
                        }}
                      >
                        <div style={{ flex: "1" }}>
                          <h4
                            style={{
                              fontWeight: "500",
                              color: "#1f2937",
                              fontSize: "0.9rem",
                              margin: "0 0 4px 0",
                            }}
                          >
                            {item.name}
                          </h4>
                          <p
                            style={{
                              fontSize: "0.8rem",
                              color: "#6b7280",
                              margin: "0 0 2px 0",
                            }}
                          >
                            ⏱️ {item.timeToImplement}
                          </p>
                          {(item.pages && item.pages.trim()) ||
                          (item.blocks && item.blocks.trim()) ? (
                            <p
                              style={{
                                fontSize: "0.7rem",
                                color: "#6b7280",
                                margin: "0",
                              }}
                            >
                              {item.pages &&
                                item.pages.trim() &&
                                `📄 ${item.pages}`}
                              {item.pages &&
                                item.pages.trim() &&
                                item.blocks &&
                                item.blocks.trim() &&
                                " | "}
                              {item.blocks &&
                                item.blocks.trim() &&
                                `🧱 ${item.blocks}`}
                            </p>
                          ) : null}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span
                              style={{ fontWeight: "600", color: "#059669" }}
                            >
                              ${(item.price * item.quantity).toLocaleString()}
                            </span>
                            <button
                              onClick={() => removeFromQuote(item.id)}
                              style={{
                                color: "#ef4444",
                                fontSize: "0.8rem",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "4px",
                              }}
                            >
                              ✕
                            </button>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "0.8rem",
                              color: "#6b7280",
                            }}
                          >
                            <span>Qty:</span>
                            <button
                              onClick={() =>
                                updateQuantity(item.id, item.quantity - 1)
                              }
                              disabled={item.quantity <= 1}
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "4px",
                                border: "1px solid #d1d5db",
                                backgroundColor:
                                  item.quantity <= 1 ? "#f3f4f6" : "white",
                                color:
                                  item.quantity <= 1 ? "#9ca3af" : "#374151",
                                cursor:
                                  item.quantity <= 1
                                    ? "not-allowed"
                                    : "pointer",
                                fontSize: "0.7rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              −
                            </button>
                            <span
                              style={{
                                minWidth: "20px",
                                textAlign: "center",
                                fontWeight: "500",
                              }}
                            >
                              {item.quantity}
                            </span>
                            <button
                              onClick={() =>
                                updateQuantity(item.id, item.quantity + 1)
                              }
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "4px",
                                border: "1px solid #d1d5db",
                                backgroundColor: "white",
                                color: "#374151",
                                cursor: "pointer",
                                fontSize: "0.7rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              +
                            </button>
                            <span
                              style={{ fontSize: "0.7rem", marginLeft: "4px" }}
                            >
                              (${item.price.toLocaleString()} each)
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      borderTop: "1px solid #e5e7eb",
                      paddingTop: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <span style={{ color: "#6b7280" }}>Subtotal:</span>
                      <span style={{ fontWeight: "600" }}>
                        ${totalCost.toLocaleString()}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        color: "#1f2937",
                      }}
                    >
                      <span>Total:</span>
                      <span style={{ color: "#059669" }}>
                        ${totalCost.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <button
                    style={{
                      width: "100%",
                      marginTop: "24px",
                      backgroundColor: "#059669",
                      color: "white",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      fontWeight: "500",
                      border: "none",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#047857")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "#059669")
                    }
                  >
                    Generate Quote Document
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
